import { useState } from "react";
import { useCustomerOrder } from "../hooks/useCustomerOrder";
import { moneyFormatter } from "../lib/money";
import "./PaymentPage.css";

type PaymentPageProps = {
  orderCode: string;
  onOpenOrder: (orderCode: string) => void;
};

function readableStatus(status: string) {
  return status.replaceAll("_", " ");
}

async function copyText(value: string, label: string, onCopied: (message: string) => void) {
  if (!value) {
    return;
  }

  try {
    await navigator.clipboard.writeText(value);
    onCopied(`${label} copied.`);
  } catch {
    onCopied(`${label}: ${value}`);
  }
}

export function PaymentPage({ orderCode, onOpenOrder }: PaymentPageProps) {
  const { order, refreshOrder, status } = useCustomerOrder({ autoRefresh: true, orderCode });
  const [copyStatus, setCopyStatus] = useState("");

  if (status && !order) {
    return (
      <section className="commerce-panel payment-panel" aria-labelledby="payment-title">
        <article className="payment-card">
          <p className="eyebrow">Payment</p>
          <h1 id="payment-title">Order access needed</h1>
          <p>{status}</p>
        </article>
      </section>
    );
  }

  if (!order) {
    return (
      <section className="commerce-panel payment-panel" aria-labelledby="payment-title">
        <article className="payment-card">
          <p className="eyebrow">Payment</p>
          <h1 id="payment-title">Loading payment details</h1>
          <p>Checking the order reference.</p>
        </article>
      </section>
    );
  }

  const instructions = order.paymentInstructions;
  const isPaid = order.paymentStatus === "paid";
  const businessLabel = instructions.businessLabel ?? "Paybill";
  const businessNumber = instructions.businessNumber ?? instructions.paybill;

  return (
    <section className="commerce-panel payment-panel" aria-labelledby="payment-title">
      <div className="payment-grid">
        <article className="payment-card payment-card--primary">
          <div className="payment-card__heading">
            <div>
              <p className="eyebrow">Payment</p>
              <h1 id="payment-title">{isPaid ? "Payment confirmed" : "Complete payment"}</h1>
            </div>
            <span className="role-pill">{readableStatus(order.paymentStatus)}</span>
          </div>

          <dl className="payment-instructions">
            <div>
              <dt>Amount</dt>
              <dd>{moneyFormatter.format(instructions.amountKes)}</dd>
            </div>
            <div>
              <dt>{businessLabel}</dt>
              <dd>{businessNumber || "Setup pending"}</dd>
            </div>
            <div>
              <dt>Account</dt>
              <dd>{instructions.accountReference}</dd>
            </div>
          </dl>
          <p>
            Pay with M-Pesa Paybill, enter this account number, then wait here for confirmation.
          </p>

          <div className="payment-actions">
            <button
              className="button button--secondary"
              disabled={!businessNumber}
              type="button"
              onClick={() => void copyText(businessNumber, businessLabel, setCopyStatus)}
            >
              Copy {businessLabel}
            </button>
            <button
              className="button button--secondary"
              type="button"
              onClick={() => void copyText(instructions.accountReference, "Account", setCopyStatus)}
            >
              Copy Account
            </button>
            <button className="button" disabled={isPaid} type="button" onClick={() => void refreshOrder()}>
              Check payment
            </button>
          </div>

          {copyStatus ? <p className="form-status">{copyStatus}</p> : null}
          {status ? <p className="form-status">{status}</p> : null}
        </article>

        <article className="payment-card">
          <p className="eyebrow">Order</p>
          <h2>{order.orderCode}</h2>
          <dl className="payment-meta">
            <div>
              <dt>Status</dt>
              <dd>{readableStatus(order.status)}</dd>
            </div>
            <div>
              <dt>Items</dt>
              <dd>{order.itemCount}</dd>
            </div>
            <div>
              <dt>Phone</dt>
              <dd>{order.phone}</dd>
            </div>
            <div>
              <dt>Fulfillment</dt>
              <dd>{readableStatus(order.fulfillmentMethod)}</dd>
            </div>
          </dl>
          <button className="button button--ghost" type="button" onClick={() => onOpenOrder(order.orderCode ?? orderCode)}>
            View order
          </button>
        </article>
      </div>
    </section>
  );
}
